import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { JsonPipe } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OtapUpgradeService } from '@services/otap-upgrade/otap.upgrade.service';
import { OtapUpgradeGatewayDTO } from '@app/shared/models/otap-upgrade-gateway.inerface';
import { OtapUpgradeTaskDTO } from '@app/shared/models/otap-upgrade-task.interface';
import { OtapUpgradeDTO } from '@app/shared/models/otap.upgrade.interface';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { OtapWebsocketService } from '@services/otap-upgrade/otap-websocket-service';
import { GatewayHealthDTO, GatewayMetricDTO, JobNotification } from '@app/shared/models/gateway-health-dto.interface';
import type { IMessage } from '@stomp/stompjs';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatCard } from '@angular/material/card';
import { GatewayDetailsComponent } from './gateway-details/gateway-details.component';
import { JobTasksComponent } from './job-tasks/job-tasks.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { CopyToClipboardService } from '@services/copy-to-clipboard.service';
import { ToastService } from '@services/toast/toast.service';

interface OtapNotification {
  timestamp: string;
  gateway: string;
  message: Record<string, unknown>;
}

@Component({
  selector: 'app-job-information',
  templateUrl: './job-information.component.html',
  styleUrls: ['./job-information.component.scss'],
  imports: [
    JsonPipe,
    MatTabGroup,
    MatTab,
    MatCard,
    GatewayDetailsComponent,
    JobTasksComponent,
    MatButton,
    MatIconButton,
    MatIcon,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobInformationComponent implements OnInit, OnDestroy {
  private readonly otapUpgradeService = inject(OtapUpgradeService);
  private readonly otapWebsocketService = inject(OtapWebsocketService);
  private readonly copyToClipboardService = inject(CopyToClipboardService);
  private readonly toastService = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectedJob = toSignal(this.otapUpgradeService.selectedJob$, { initialValue: null });

  readonly job = linkedSignal(() => this.selectedJob());
  readonly gateways = signal<OtapUpgradeGatewayDTO[]>([]);
  readonly tasks = signal<OtapUpgradeTaskDTO[]>([]);
  readonly jobStartEnabled = signal(true);
  readonly exportStatus = signal<{ exportId: string; complete: boolean } | null>(null);
  readonly exportCompleteEvent = output<{ exportId: string; complete: boolean }>();
  readonly otapNotifications = signal<OtapNotification[]>([]);
  readonly notificationsExpanded = signal(true);
  private readonly jobRunnable = signal(false);
  private readonly job$ = toObservable(this.job);
  private pendingOtapNotifications: OtapNotification[] = [];
  private otapNotificationFlushHandle?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    const init$ = this.route.paramMap.pipe(
      switchMap(params => {
        const id = params.get('jobId');
        if (!id) return of(null);
        // If already selected and matches, reuse
        return this.otapUpgradeService.selectedJob$.pipe(
          take(1),
          switchMap(cur => {
            if (cur && String(cur.id) === id) return of(cur);
            // Otherwise fetch and set
            return this.otapUpgradeService.getJob(Number(id)).pipe(
              tap(job => this.otapUpgradeService.updateSelectedJob(job)),
              catchError(() => {
                this.router.navigate(['/global-administration/otap-upgrades']);
                return of(null);
              })
            );
          })
        );
      })
    );

    init$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.job$
      .pipe(
        switchMap(job => {
          if (!job) return of<[OtapUpgradeGatewayDTO[], OtapUpgradeTaskDTO[]]>([[], []]);
          this.jobRunnable.set(job.status === 'CREATED' || job.status === 'STARTED');
          return combineLatest([
            this.otapUpgradeService.getAllGatewaysForAJob(job.id),
            this.otapUpgradeService.getAllTasksForAJob(job.id)
          ]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([gateways, tasks]) => {
        this.gateways.set(gateways);
        this.tasks.set(tasks);
        this.setStartButtonStatus(tasks);
      });

    this.initializeWebsocketListener();
  }

  toggleNotifications(accordion: MatAccordion): void {
    if (this.notificationsExpanded()) {
      accordion.closeAll();
    } else {
      accordion.openAll();
    }

    this.notificationsExpanded.update((expanded) => !expanded);
  }

  copyOtapNotifications(): void {
    this.copyToClipboardService.copyString(JSON.stringify(this.otapNotifications(), null, 2));
    this.toastService.success({
      message: 'All notifications copied to clipboard.',
      dataCy: 'copied-notifications-to-clipboard'
    });
  }

  copyOtapNotification(event: Event, notification: OtapNotification): void {
    event.stopPropagation();
    this.copyToClipboardService.copyString(JSON.stringify(notification, null, 2));
    this.toastService.success({
      message: 'Notification copied to clipboard.',
      dataCy: 'copied-notification-to-clipboard'
    });
  }

  ngOnDestroy(): void {
    if (this.otapNotificationFlushHandle) {
      clearTimeout(this.otapNotificationFlushHandle);
    }
  }

  private setStartButtonStatus(tasks: OtapUpgradeTaskDTO[]): void {
    let enableStartButtons = this.jobRunnable();
    if (tasks !== null) {
      for (const task of tasks) {
        if (task.statuses) {
          if (task.statuses.length > 0) {
            if (this.checkIfAnyTaskIsRunning(task.statuses)) {
              enableStartButtons = false;
              break;
            }
          }
        }
      }
    }
    this.jobStartEnabled.set(enableStartButtons);
  }

  onJobStartStateChanged(updatedJobStartState: boolean): void {
    this.jobStartEnabled.set(updatedJobStartState);
  }

  onExportStatusChanged(status: { exportId: string; complete: boolean }): void {
    this.exportStatus.set(status);
    this.exportCompleteEvent.emit(status);
  }

  onOtapNotification(message: string): void {
    try {
      const json: unknown = JSON.parse(message);
      if (!isRecord(json)) {
        throw new Error('Invalid OTAP notification packet');
      }

      const response = json.otapResponseNotification;
      if (!isRecord(response)) {
        throw new Error('Missing otapResponseNotification payload');
      }

      const messageFields = Object.fromEntries(
        Object.entries(response).filter(
          ([key, value]) =>
            key !== 'gatewayAddress' &&
            key !== 'receivedTime' &&
            key !== 'systemTimestamp' &&
            value !== null &&
            value !== undefined
        )
      );
      if (
        Object.keys(messageFields).length === 0 ||
        typeof response.gatewayAddress !== 'string' ||
        !isValidNotificationTimestamp(response.receivedTime)
      ) {
        throw new Error('Invalid otapResponseNotification payload');
      }

      const otapNotification: OtapNotification = {
        timestamp: new Date(response.receivedTime).toLocaleString(),
        gateway: response.gatewayAddress,
        message: messageFields
      };
      this.queueOtapNotification(otapNotification);
    } catch (error) {
      console.error('Unable to parse OTAP response notification.', error);
    }
  }

  clearOtapNotifications(): void {
    this.pendingOtapNotifications = [];
    if (this.otapNotificationFlushHandle) {
      clearTimeout(this.otapNotificationFlushHandle);
      this.otapNotificationFlushHandle = undefined;
    }
    this.otapNotifications.set([]);
  }

  private initializeWebsocketListener(): void {
    this.job$
      .pipe(
        filter((job): job is OtapUpgradeDTO => Boolean(job?.id)),
        switchMap(job => this.otapWebsocketService.listenToJob(job.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((packet) => this.handleWebsocketPacket(packet));
  }

  private handleWebsocketPacket(packet: IMessage): void {
    try {
      const parsedData = JSON.parse(packet.body) as JobNotification & { 'notification-type'?: string };
      switch (parsedData['notification-type']) {
        case 'gateway':
          this.updateGatewayData(parsedData as unknown as { gatewayId: number; sensorNodeCount: number });
          break;
        case 'gateway-health':
          this.updateGatewayHealthOrMetricData(parsedData as GatewayHealthDTO, 'gatewayHealth');
          break;
        case 'gateway-metric':
          this.updateGatewayHealthOrMetricData(parsedData as GatewayMetricDTO, 'gatewayMetric');
          break;
        case 'job-task':
          this.updateTaskData(parsedData as unknown as OtapUpgradeTaskDTO);
          break;
        case 'gateway-response':
          break;
        case 'export-complete':
          this.handleExportComplete(parsedData as unknown as { exportId: string });
          break;
        case 'otap-response-notification':
          this.onOtapNotification(packet.body);
          break;
        default:
          console.warn('Unknown notification type: ', parsedData);
      }
    } catch (error) {
      console.error('Unable to process OTAP websocket packet.', error);
    }
  }

  private queueOtapNotification(notification: OtapNotification): void {
    this.pendingOtapNotifications.push(notification);
    if (this.otapNotificationFlushHandle) {
      return;
    }

    this.otapNotificationFlushHandle = setTimeout(() => {
      const notifications = [...this.pendingOtapNotifications].reverse();
      this.pendingOtapNotifications = [];
      this.otapNotificationFlushHandle = undefined;
      this.otapNotifications.update((current) => [...notifications, ...current].slice(0, 1000));
    }, 0);
  }

  private updateGatewayData(gatewayData: { gatewayId: number; sensorNodeCount: number }): void {
    this.gateways.update(gateways =>
      gateways.map(gateway =>
        gateway.id === gatewayData.gatewayId ? { ...gateway, sensorNodeCount: gatewayData.sensorNodeCount } : gateway
      )
    );
  }

  private updateGatewayHealthOrMetricData(
    gatewayData: GatewayHealthDTO | GatewayMetricDTO,
    notificationType: 'gatewayHealth' | 'gatewayMetric'
  ): void {
    this.gateways.update(gateways =>
      gateways.map(gateway =>
        gateway.id !== gatewayData.gatewayId
          ? gateway
          : notificationType === 'gatewayHealth'
            ? { ...gateway, gatewayHealth: gatewayData as GatewayHealthDTO }
            : { ...gateway, gatewayMetric: gatewayData as GatewayMetricDTO }
      )
    );
  }

  private updateTaskData(taskData: OtapUpgradeTaskDTO): void {
    this.tasks.update(tasks => tasks.map(task => (task.id === taskData.id ? taskData : task)));
    this.setStartButtonStatus(this.tasks());
  }

  checkTaskEndDate(taskStatuses: OtapUpgradeTaskDTO['statuses']): void {
    let allTasksEnded = true;
    for (const status of taskStatuses) {
      if (!status.endedDateTime || status.endedDateTime === null) {
        allTasksEnded = false;
        break;
      }
    }

    this.jobStartEnabled.set(allTasksEnded);
  }

  checkIfAnyTaskIsRunning(taskStatuses: OtapUpgradeTaskDTO['statuses']): boolean {
    return taskStatuses.some(status => !status.endedDateTime);
  }

  private handleExportComplete(notification: { exportId: string }): void {
    const exportId = notification.exportId;
    this.onExportStatusChanged({ exportId, complete: true });
  }

}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidNotificationTimestamp(value: unknown): value is string | number {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}
